import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bell,
    BellOff,
    Check,
    CheckCheck,
    RefreshCw,
    Filter,
    Clock,
    ChevronRight
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getNotificationRoute, getNotificationConfig, resolveNotificationNavigation } from '../utils/notificationHelpers';
import LiquidacionModal from './VentaEspecial/LiquidacionModal';

const NotificationsView = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const liquidacionId = searchParams.get('liquidacion');
    const { markAsRead, markAllAsRead, fetchUnreadCount, latestNotification } = useNotifications();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [soloNoLeidas, setSoloNoLeidas] = useState(false);

    // Agregar notificacion en tiempo real cuando llega via socket
    useEffect(() => {
        if (!latestNotification) return;
        setNotifications(prev => {
            // Evitar duplicados
            if (prev.some(n => n.id === latestNotification.id)) return prev;
            return [latestNotification, ...prev];
        });
        setTotalCount(prev => prev + 1);
    }, [latestNotification]);

    const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
        if (!user?.id) return;

        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/notificaciones', {
                params: { page: pageNum, per_page: 20, solo_no_leidas: soloNoLeidas }
            });
            const data = response.data;

            if (append) {
                setNotifications(prev => [...prev, ...data.notificaciones]);
            } else {
                setNotifications(data.notificaciones);
            }

            setTotalCount(data.total);
            setHasMore(pageNum < data.pages);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.id, soloNoLeidas]);

    useEffect(() => {
        setPage(1);
        fetchNotifications(1, false);
    }, [fetchNotifications]);

    const handleNotificationClick = async (notification) => {
        // Marcar como leída si no lo está
        if (!notification.leido) {
            const success = await markAsRead(notification.id);
            if (success) {
                setNotifications(prev =>
                    prev.map(n => n.id === notification.id ? { ...n, leido: true } : n)
                );
            }
        }

        // Navegar al recurso (tareas requieren resolución async para construir la URL)
        if (notification.referencia_tipo === 'tarea' && notification.referencia_id) {
            const path = await resolveNotificationNavigation(notification);
            navigate(path);
        } else {
            navigate(getNotificationRoute(notification));
        }
    };

    const handleMarkAsRead = async (e, notification) => {
        e.stopPropagation();
        if (notification.leido) return;

        const success = await markAsRead(notification.id);
        if (success) {
            setNotifications(prev =>
                prev.map(n => n.id === notification.id ? { ...n, leido: true } : n)
            );
        }
    };

    const handleMarkAllAsRead = async () => {
        const success = await markAllAsRead();
        if (success) {
            setNotifications(prev => prev.map(n => ({ ...n, leido: true })));
        }
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(nextPage, true);
    };

    const handleRefresh = () => {
        setPage(1);
        fetchNotifications(1, false);
        fetchUnreadCount();
    };

    const getNotificationIcon = (tipo) => {
        const config = getNotificationConfig(tipo);
        const IconComponent = config.icon;
        return <IconComponent size={20} className={config.color} />;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Ahora mismo';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHours < 24) return `Hace ${diffHours}h`;
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `Hace ${diffDays} días`;

        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const unreadCount = notifications.filter(n => !n.leido).length;

    return (
        <div className="max-w-4xl mx-auto animate-fadeIn pb-8">
            {/* Header */}
            <header className="mb-8 pt-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-lg shadow-blue-200">
                            <Bell size={28} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Notificaciones</h1>
                            <p className="text-gray-500 text-sm mt-1">
                                {totalCount} notificaciones • {unreadCount} sin leer
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <motion.button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-all disabled:opacity-50"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Actualizar"
                        >
                            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                        </motion.button>

                        <motion.button
                            onClick={() => setSoloNoLeidas(v => !v)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                                soloNoLeidas
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            title={soloNoLeidas ? 'Ver todas' : 'Ver solo no leídas'}
                        >
                            <Filter size={16} />
                            Solo no leídas
                        </motion.button>

                        {unreadCount > 0 && (
                            <motion.button
                                onClick={handleMarkAllAsRead}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium text-sm transition-all"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <CheckCheck size={18} />
                                Marcar todas
                            </motion.button>
                        )}
                    </div>
                </div>
            </header>

            {/* Notifications List */}
            <div className="space-y-3">
                {loading && notifications.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                        <div className="inline-block animate-spin w-8 h-8 border-4 border-gray-100 border-t-blue-500 rounded-full mb-3"></div>
                        <p className="text-gray-500">Cargando notificaciones...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="bg-white rounded-2xl p-16 text-center border border-gray-100">
                        <BellOff size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">
                            {soloNoLeidas ? 'No tienes notificaciones sin leer' : 'No hay notificaciones'}
                        </h3>
                        <p className="text-gray-400 text-sm">
                            {soloNoLeidas ? 'Estás al día' : 'Las nuevas publicaciones aparecerán aquí'}
                        </p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {notifications.map((notification, index) => {
                            const config = getNotificationConfig(notification.tipo);
                            return (
                                <motion.div
                                    key={notification.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ delay: index * 0.03 }}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`
                                        relative rounded-2xl p-5 border transition-all cursor-pointer group
                                        ${notification.leido
                                            ? 'bg-white border-gray-100 opacity-70 hover:opacity-100'
                                            : 'border-gray-100 shadow-sm hover:shadow-md'
                                        }
                                    `}
                                    style={!notification.leido ? {
                                        background: `linear-gradient(90deg, ${config.accent}10 0%, white 40%)`,
                                    } : undefined}
                                >
                                    <div className="flex items-start gap-4">
                                        {/* Icon */}
                                        <div className={`
                                            p-3 rounded-xl shrink-0
                                            ${notification.leido ? 'bg-gray-100' : config.bg}
                                        `}>
                                            {getNotificationIcon(notification.tipo)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className={`font-semibold ${notification.leido ? 'text-gray-600' : 'text-gray-900'}`}>
                                                    {notification.titulo}
                                                </h3>
                                                {!notification.leido && (
                                                    <span
                                                        className="w-2.5 h-2.5 rounded-full animate-pulse shrink-0"
                                                        style={{ backgroundColor: config.accent, boxShadow: `0 0 0 3px ${config.accent}1f` }}
                                                    />
                                                )}
                                            </div>

                                            {notification.mensaje && (
                                                <p className={`text-sm line-clamp-2 mb-2 ${notification.leido ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {notification.mensaje}
                                                </p>
                                            )}

                                            <div className="flex items-center gap-3 text-xs text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <Clock size={12} />
                                                    {formatDate(notification.created_at)}
                                                </span>
                                                {notification.leido && (
                                                    <span className="flex items-center gap-1 text-green-500">
                                                        <Check size={12} />
                                                        Leído
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 shrink-0">
                                            {!notification.leido && (
                                                <motion.button
                                                    onClick={(e) => handleMarkAsRead(e, notification)}
                                                    className="p-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    title="Marcar como leída"
                                                >
                                                    <Check size={18} />
                                                </motion.button>
                                            )}
                                            <ChevronRight
                                                size={18}
                                                className="text-gray-300 group-hover:text-gray-500 transition-colors"
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                )}

                {/* Load More Button */}
                {hasMore && notifications.length > 0 && (
                    <motion.button
                        onClick={handleLoadMore}
                        disabled={loading}
                        className="w-full py-4 bg-gray-50 hover:bg-gray-100 rounded-2xl text-gray-600 font-medium transition-all disabled:opacity-50"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <RefreshCw size={16} className="animate-spin" />
                                Cargando...
                            </span>
                        ) : (
                            'Cargar más notificaciones'
                        )}
                    </motion.button>
                )}
            </div>

            {liquidacionId && (
                <LiquidacionModal
                    pedidoId={liquidacionId}
                    onClose={() => setSearchParams({}, { replace: true })}
                />
            )}
        </div>
    );
};

export default NotificationsView;
